<script>
  import { max, range } from 'd3-array';
  import { scaleBand, scaleLinear } from 'd3-scale';
  import { line as lineShape } from 'd3-shape';
  import { intcomma } from 'journalize';
  import { dateFormat } from '../../utils/d3-utils';
  import { __ } from '../../utils/i18n';

  export let bars;
  export let line;
  export let barFill;
  export let lineStroke;
  export let globalMax;
  export let showXAxisLabels;
  export let annotation;
  export let showAnnotations;
  export let total;
  export let maxDate;
  export let locale = 'en';

  const count = bars.length;

  const xMax = count;
  const xRange = range(count);

  const width = 175;
  const height = 140;
  const margin = { top: 0, right: 7.5, bottom: 0, left: 7.5 };

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xScale = scaleBand().domain(xRange).range([0, innerWidth]).padding(0.1);

  $: yMax = globalMax ? globalMax : max(bars);
  $: yScale = scaleLinear().domain([0, yMax]).range([innerHeight, 0]);

  const cappedY = (value) => {
    let y;
    if (value < 0) {
      y = yScale(0);
    } else {
      y = yScale(value);
    }
    // Cut any extreme outliers
    if (y < -200) {
      return 0;
      // Cut any bars less than zero
    } else if (y > innerHeight) {
      return innerHeight;
    }
    return y;
  };
  
  const cappedTotal = (value) => {
    if (value < 0) {
      return 0
    } else {
      return value;
    }
  };

  const bandwidth = xScale.bandwidth();

  const createLine = lineShape()
    .defined((d) => !isNaN(d))
    .x((d, idx) => xScale(idx) + bandwidth / 2)
    .y((d) => d > 0 ? yScale(d) : yScale(0));
</script>

<div class="small-chart">
  <div class="static-chart">
    <svg {width} {height} role="graphics-document">
      <g transform="translate({margin.left},{margin.top})">
        <g class="bars" role="graphics-object">
          {#each bars as total, idx}
            <rect
              data-y={total}
              data-x={idx}
              fill={barFill}
              fill-opacity="0.5"
              x={xScale(idx)}
              y={cappedY(total)}
              height={yScale(0) - cappedY(total)}
              width={bandwidth}
              role="graphics-symbol"
            />
          {/each}
        </g>
        <g class="line" role="graphics-symbol">
          <path
            d={createLine(line)}
            fill="none"
            stroke={lineStroke}
            stroke-width="2"
            stroke-linecap="join"
            stroke-linejoin="join"
          />
        </g>
        <g role="graphics-object">
          <text
            class="small-chart-label--subhead small-chart-label--subhead--halo"
            transform="translate(10, 12)"
          >
            <tspan text-anchor="left">
              {intcomma(cappedTotal(total).toFixed(1))}
            </tspan>
            <tspan
              text-anchor="left"
              x="0"
              dy="16"
              class:display-none={!showAnnotations}
            >
              {__(annotation)}
            </tspan>
            <tspan
              text-anchor="left"
              x="0"
              dy="16"
              class:display-none={!showAnnotations}
            >
              {__('in last 7 days')}
            </tspan>
          </text>
        </g>
        <g role="graphics-object">
          <text
            class="small-chart-label--subhead"
            transform="translate(10, 12)"
          >
            <tspan text-anchor="left">
              {intcomma(cappedTotal(total).toFixed(1))}
            </tspan>
            <tspan
              text-anchor="left"
              x="0"
              dy="16"
              class:display-none={!showAnnotations}
            >
              {__(annotation)}
            </tspan>
            <tspan
              text-anchor="left"
              x="0"
              dy="16"
              class:display-none={!showAnnotations}
            >
              {__('in last 7 days')}
            </tspan>
          </text>
        </g>
        <g
          class="annotations annotations--halo"
          class:display-none={!showAnnotations}
          role="graphics-object"
        >
          <g>
            <text
              text-anchor="middle"
              transform="translate(25, {innerHeight - 36})"
            >
              <tspan dx="0" dy="0">7-day</tspan>
              <tspan x="0" dy="13">average</tspan>
              <tspan x="0" dy="13">│</tspan>
            </text>
          </g>
        </g>
        <g
          class="annotations"
          class:display-none={!showAnnotations}
          role="graphics-object"
        >
          <g>
            <text
              text-anchor="middle"
              transform="translate(25, {innerHeight - 36})"
            >
              {#if locale == 'es'}
                <tspan dx="0" dy="0">Promedio</tspan>
                <tspan x="0" dy="13">de 7 días</tspan>
                <tspan x="0" dy="13">│</tspan>
              {:else}
                <tspan dx="0" dy="0">7-day</tspan>
                <tspan x="0" dy="13">average</tspan>
                <tspan x="0" dy="13">│</tspan>
              {/if}
            </text>
          </g>
        </g>
        <g
          class="axes"
          class:display-none={!showXAxisLabels}
          role="graphics-symbol"
        >
          <g transform="translate(10, {innerHeight + 12.5})">
            <text text-anchor="start">{__('March')} 1<text /></text></g
          >
          <g transform="translate({innerWidth}, {innerHeight + 12.5})">
            <text text-anchor="end"
              >{dateFormat(maxDate)}<text /></text
            ></g
          >
        </g>
      </g>
    </svg>
  </div>
</div>
